import { useContext, useEffect, useState } from 'react'

import cn from 'classnames'

import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks'
import { WalletActionButton } from '@tronweb3/tronwallet-adapter-react-ui'

import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { AuthProviderContext } from '@src/providers/AuthProvider'
import { isOriginSolana, isOriginTron } from '@src/utils/chainUtils'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore } from '@src/zustand_stores/application.store'

interface ConnectButtonProps
    extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    isEvmOnly?: boolean
    overrideEvmButtonLabel?: string
}

const ConnectButton = ({ className, isEvmOnly = true, overrideEvmButtonLabel, ...props }: ConnectButtonProps) => {
    // Connection handlers.
    const { connectHandler } = useContext(AuthProviderContext)
    const { connect: connectSolanaWallet } = useSolanaWallet()
    const { connect: connectTronWallet } = useTronWallet()
    const { walletButtonDisplayChainId } = useApplicationStore()

    const evmButtonLabel = overrideEvmButtonLabel || 'Connect Wallet'

    const {
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        EVMChainId,
        evmWalletAddress,
        isAnyWalletConnected,
    } = useWalletConnections()

    const [networkImage, setNetworkImage] = useState(`assets/network/${getChainNameFromChainId(1)}.png`)
    const [truncatedAddress, setTruncatedAddress] = useState('')

    const isSolanaChain = isOriginSolana(walletButtonDisplayChainId)
    const isTronChain = isOriginTron(walletButtonDisplayChainId)

    const openWalletDrawer = useApplicationStore(state => state.openWalletDrawer)

    const requestConnection = async (chainId: number) => {
        if (chainId === ChainId.Tron) {
            await connectTronWallet()
        } else if (chainId === ChainId.Solana) {
            await connectSolanaWallet()
        } else {
            await connectHandler()
        }
    }

    const handleConnectedClick = () => {
        openWalletDrawer(true)
    }

    useEffect(() => {
        if (isEvmOnly) {
            if (isEVMConnected && evmWalletAddress && EVMChainId) {
                setNetworkImage(`assets/network/${getChainNameFromChainId(EVMChainId)}.png`)
                setTruncatedAddress(truncateAddress(evmWalletAddress))
            }
        } else {
            if (isSolanaConnected && solanaWalletAddress) {
                setNetworkImage(`assets/network/solana.png`)
                setTruncatedAddress(truncateAddress(solanaWalletAddress))
            }
            if (isTronConnected && tronWalletAddress) {
                setNetworkImage(`assets/network/tron.png`)
                setTruncatedAddress(truncateAddress(tronWalletAddress))
            }
        }
    }, [
        isTronConnected,
        tronWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isEVMConnected,
        EVMChainId,
        evmWalletAddress,
        isAnyWalletConnected,
        walletButtonDisplayChainId,
    ])

    return (
        <div className='relative'>
            {((): JSX.Element => {
                if (isEvmOnly) {
                    if (!isEVMConnected) {
                        return (
                            <button
                                className={cn('custom-wallet-button', className)}
                                onClick={e => requestConnection(1)}
                                {...props}
                            >
                                {evmButtonLabel}
                            </button>
                        )
                    } else {
                        return (
                            <button className={cn('custom-wallet-button', className)} onClick={handleConnectedClick}>
                                <img
                                    src={networkImage}
                                    alt='network'
                                    className='h-6 w-6'
                                    onError={e => {
                                        e.currentTarget.src = '/assets/token/unknown.png'
                                    }}
                                />
                                <span>{truncatedAddress}</span>
                            </button>
                        )
                    }
                } else {
                    if (isTronChain && !isTronConnected) {
                        return (
                            <WalletActionButton className={cn('custom-wallet-button', className)}>
                                <img src={'assets/network/tron.png'} alt='network' className='h-6 w-6' />
                                Connect Tron Wallet
                            </WalletActionButton>
                        )
                    } else if (isSolanaChain && !isSolanaConnected) {
                        return (
                            <WalletMultiButton
                                className={cn('w-full', className)}
                                style={{ height: '56px', fontSize: '16px' }}
                            >
                                <img src={'assets/network/solana.png'} alt='network' className='h-6 w-6' />
                                Connect Solana Wallet
                            </WalletMultiButton>
                        )
                    } else if (!isEVMConnected) {
                        return (
                            <button
                                className={cn('custom-wallet-button', className)}
                                onClick={e => requestConnection(1)}
                                {...props}
                            >
                                {evmButtonLabel}
                            </button>
                        )
                    } else {
                        return (
                            <button className={cn('custom-wallet-button', className)} onClick={handleConnectedClick}>
                                <img src={networkImage} alt='network' className='h-6 w-6' />
                                <span>{truncatedAddress}</span>
                            </button>
                        )
                    }
                }
            })()}
        </div>
    )
}

export default ConnectButton
